import { useRef, useEffect, useCallback, useState, memo } from 'react';
import { Stage, Layer, Rect, Circle, Star, Ellipse, Text, Transformer, Line } from 'react-konva';
import { useAnimation } from '@/context/AnimationContext';
import { getObjectPropsAtFrame } from '@/utils/interpolation';
import { BatelkaCharacter } from '@/components/editor/BatelkaCharacter';
import { AgataCharacter } from '@/components/editor/AgataCharacter';
import { BlondeBallerina } from '@/components/editor/BlondeBallerina';
import { SunCharacter } from '@/components/editor/SunCharacter';
import { SingerGirl } from '@/components/editor/SingerGirl';
import { StickmanCharacter } from '@/components/editor/StickmanCharacter';

const CanvasObject = memo(function CanvasObject({ obj, frame, isSelected, onSelect, onDragEnd, onTransformEnd }) {
  const shapeRef = useRef(null);
  const trRef = useRef(null);
  const props = getObjectPropsAtFrame(obj, frame);

  useEffect(() => {
    if (isSelected && trRef.current && shapeRef.current) {
      trRef.current.nodes([shapeRef.current]);
      trRef.current.getLayer().batchDraw();
    }
  }, [isSelected]);

  const handleDragEnd = (e) => onDragEnd(obj.id, { x: e.target.x(), y: e.target.y() });
  const handleTransformEnd = () => {
    const node = shapeRef.current;
    if (!node) return;
    onTransformEnd(obj.id, {
      x: node.x(), y: node.y(), rotation: node.rotation(),
      scaleX: node.scaleX(), scaleY: node.scaleY(),
    });
  };

  const commonProps = {
    ref: shapeRef,
    x: props.x, y: props.y,
    rotation: props.rotation, scaleX: props.scaleX, scaleY: props.scaleY,
    opacity: props.opacity, draggable: true,
    onClick: () => onSelect(obj.id), onTap: () => onSelect(obj.id),
    onDragEnd: handleDragEnd, onTransformEnd: handleTransformEnd,
  };

  let shape = null;
  switch (obj.type) {
    case 'rect':
      shape = <Rect {...commonProps} width={obj.width} height={obj.height} offsetX={obj.width / 2} offsetY={obj.height / 2} fill={obj.fill} stroke={obj.stroke || undefined} strokeWidth={obj.strokeWidth || 0} cornerRadius={4} />;
      break;
    case 'circle':
      shape = <Circle {...commonProps} radius={obj.width / 2} fill={obj.fill} stroke={obj.stroke || undefined} strokeWidth={obj.strokeWidth || 0} />;
      break;
    case 'ellipse':
      shape = <Ellipse {...commonProps} radiusX={obj.width / 2} radiusY={obj.height / 2} fill={obj.fill} stroke={obj.stroke || undefined} strokeWidth={obj.strokeWidth || 0} />;
      break;
    case 'star':
      shape = <Star {...commonProps} numPoints={obj.numPoints || 5} innerRadius={obj.innerRadius || 15} outerRadius={obj.outerRadius || 30} fill={obj.fill} stroke={obj.stroke || undefined} strokeWidth={obj.strokeWidth || 0} />;
      break;
    case 'text':
      shape = <Text {...commonProps} text={obj.text || 'Text'} fontSize={obj.fontSize || 24} fill={obj.fill} fontFamily="DM Sans" />;
      break;
    case 'path':
      shape = <Line {...commonProps} points={obj.points || []} closed={!!obj.fill} fill={obj.fill || undefined} stroke={obj.stroke || obj.fill || '#ffffff'} strokeWidth={obj.strokeWidth || 3} lineCap="round" lineJoin="round" tension={0} />;
      break;
    case 'character':
      return (
        <BatelkaCharacter
          x={props.x} y={props.y}
          scaleX={props.scaleX} scaleY={props.scaleY}
          rotation={props.rotation} opacity={props.opacity}
          leftArmAngle={props.leftArmAngle} rightArmAngle={props.rightArmAngle}
          leftLegAngle={props.leftLegAngle} rightLegAngle={props.rightLegAngle}
          fill={obj.fill} customColors={obj.customColors} draggable={true}
          onClick={() => onSelect(obj.id)}
          onDragEnd={(e) => onDragEnd(obj.id, { x: e.target.x(), y: e.target.y() })}
          onTransformEnd={() => {}}
        />
      );
    case 'agata':
      return (
        <AgataCharacter
          x={props.x} y={props.y}
          scaleX={props.scaleX} scaleY={props.scaleY}
          rotation={props.rotation} opacity={props.opacity}
          fill={obj.fill} customColors={obj.customColors} draggable={true}
          onClick={() => onSelect(obj.id)}
          onDragEnd={(e) => onDragEnd(obj.id, { x: e.target.x(), y: e.target.y() })}
          onTransformEnd={() => {}}
        />
      );
    case 'blondeBallerina':
      return (
        <BlondeBallerina
          x={props.x} y={props.y}
          scaleX={props.scaleX} scaleY={props.scaleY}
          rotation={props.rotation} opacity={props.opacity}
          leftArmAngle={props.leftArmAngle} rightArmAngle={props.rightArmAngle}
          leftLegAngle={props.leftLegAngle} rightLegAngle={props.rightLegAngle}
          fill={obj.fill} customColors={obj.customColors} draggable={true}
          onClick={() => onSelect(obj.id)}
          onDragEnd={(e) => onDragEnd(obj.id, { x: e.target.x(), y: e.target.y() })}
          onTransformEnd={() => {}}
        />
      );
    case 'sun':
      return (
        <SunCharacter
          x={props.x} y={props.y}
          scaleX={props.scaleX} scaleY={props.scaleY}
          rotation={props.rotation} opacity={props.opacity}
          leftArmAngle={props.leftArmAngle} rightArmAngle={props.rightArmAngle}
          fill={obj.fill} customColors={obj.customColors} draggable={true}
          onClick={() => onSelect(obj.id)}
          onDragEnd={(e) => onDragEnd(obj.id, { x: e.target.x(), y: e.target.y() })}
          onTransformEnd={() => {}}
        />
      );
    case 'singer':
      return (
        <SingerGirl
          x={props.x} y={props.y}
          scaleX={props.scaleX} scaleY={props.scaleY}
          rotation={props.rotation} opacity={props.opacity}
          leftArmAngle={props.leftArmAngle} rightArmAngle={props.rightArmAngle}
          leftLegAngle={props.leftLegAngle} rightLegAngle={props.rightLegAngle}
          fill={obj.fill} customColors={obj.customColors} draggable={true}
          onClick={() => onSelect(obj.id)}
          onDragEnd={(e) => onDragEnd(obj.id, { x: e.target.x(), y: e.target.y() })}
          onTransformEnd={() => {}}
        />
      );
    case 'stickman':
      return (
        <StickmanCharacter
          x={props.x} y={props.y}
          scaleX={props.scaleX} scaleY={props.scaleY}
          rotation={props.rotation} opacity={props.opacity}
          headAngle={props.headAngle}
          leftArmAngle={props.leftArmAngle} rightArmAngle={props.rightArmAngle}
          leftForearmAngle={props.leftForearmAngle} rightForearmAngle={props.rightForearmAngle}
          leftLegAngle={props.leftLegAngle} rightLegAngle={props.rightLegAngle}
          leftShinAngle={props.leftShinAngle} rightShinAngle={props.rightShinAngle}
          mouthWidth={props.mouthWidth} eyebrowAngle={props.eyebrowAngle}
          fill={obj.fill} draggable={true}
          onClick={() => onSelect(obj.id)}
          onDragEnd={(e) => onDragEnd(obj.id, { x: e.target.x(), y: e.target.y() })}
          onTransformEnd={() => {}}
        />
      );
    default:
      shape = <Rect {...commonProps} width={obj.width || 50} height={obj.height || 50} offsetX={(obj.width || 50) / 2} offsetY={(obj.height || 50) / 2} fill={obj.fill || '#666'} />;
  }

  return (
    <>
      {shape}
      {isSelected && obj.type !== 'character' && (
        <Transformer
          ref={trRef} rotateEnabled={true}
          enabledAnchors={['top-left', 'top-right', 'bottom-left', 'bottom-right', 'middle-left', 'middle-right', 'top-center', 'bottom-center']}
          boundBoxFunc={(oldBox, newBox) => (newBox.width < 5 || newBox.height < 5) ? oldBox : newBox}
          borderStroke="#f472b6" borderStrokeWidth={1.5}
          anchorFill="#f472b6" anchorStroke="#ec4899" anchorSize={7}
        />
      )}
    </>
  );
});

export function AnimationCanvas() {
  const { state, dispatch } = useAnimation();
  const stageRef = useRef(null);
  const containerRef = useRef(null);
  const [penPoints, setPenPoints] = useState([]);
  const [isPenDrawing, setIsPenDrawing] = useState(false);
  const [isPanning, setIsPanning] = useState(false);
  const panStartRef = useRef({ x: 0, y: 0 });

  const handleSelect = useCallback((objectId) => dispatch({ type: 'SELECT_OBJECT', payload: objectId }), [dispatch]);
  const handleDeselect = (e) => {
    if (e.target === e.target.getStage() && state.activeTool === 'select') {
      dispatch({ type: 'SELECT_OBJECT', payload: null });
    }
  };

  const handleDragEnd = useCallback((objectId, newProps) => {
    dispatch({ type: 'UPDATE_OBJECT', payload: { id: objectId, props: newProps, autoKey: true } });
  }, [dispatch]);

  const handleTransformEnd = useCallback((objectId, newProps) => {
    dispatch({ type: 'UPDATE_OBJECT', payload: { id: objectId, props: newProps, autoKey: true } });
  }, [dispatch]);

  // Zoom with mouse wheel
  const handleWheel = useCallback((e) => {
    e.preventDefault();
    const zoomFactor = e.deltaY < 0 ? 1.1 : 0.9;
    dispatch({ type: 'ZOOM_AT', payload: { newZoom: state.zoom * zoomFactor } });
  }, [dispatch, state.zoom]);

  // Pan with middle mouse / Alt+drag
  const handleContainerMouseDown = useCallback((e) => {
    if (e.button === 1 || (e.altKey && e.button === 0)) {
      e.preventDefault();
      setIsPanning(true);
      panStartRef.current = { x: e.clientX - state.panX, y: e.clientY - state.panY };
    }
  }, [state.panX, state.panY]);

  const handleContainerMouseMove = useCallback((e) => {
    if (!isPanning) return;
    dispatch({ type: 'SET_PAN', payload: { x: e.clientX - panStartRef.current.x, y: e.clientY - panStartRef.current.y } });
  }, [isPanning, dispatch]);

  const handleContainerMouseUp = useCallback(() => {
    setIsPanning(false);
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    container.addEventListener('wheel', handleWheel, { passive: false });
    return () => container.removeEventListener('wheel', handleWheel);
  }, [handleWheel]);

  // Pen tool handlers
  const handleStageMouseDown = (e) => {
    if (state.activeTool !== 'pen') return;
    const pos = e.target.getStage().getPointerPosition();
    if (!pos) return;
    const scaledX = (pos.x - state.panX) / state.zoom;
    const scaledY = (pos.y - state.panY) / state.zoom;

    if (!isPenDrawing) {
      setPenPoints([scaledX, scaledY]);
      setIsPenDrawing(true);
    } else {
      setPenPoints(prev => [...prev, scaledX, scaledY]);
    }
  };

  const handleStageMouseMove = (e) => {
    if (state.activeTool !== 'pen' || !isPenDrawing) return;
    // Preview: we show cursor movement
  };

  const handleStageDoubleClick = () => {
    if (state.activeTool === 'pen' && isPenDrawing && penPoints.length >= 4) {
      finishPenPath();
    }
  };

  const finishPenPath = () => {
    if (penPoints.length < 4) { setPenPoints([]); setIsPenDrawing(false); return; }
    const targetLayer = state.selectedLayerId || state.project.layers[0]?.id;
    if (!targetLayer) return;

    // Calculate bounds
    const xs = penPoints.filter((_, i) => i % 2 === 0);
    const ys = penPoints.filter((_, i) => i % 2 === 1);
    const cx = (Math.min(...xs) + Math.max(...xs)) / 2;
    const cy = (Math.min(...ys) + Math.max(...ys)) / 2;

    // Normalize points relative to center
    const relativePoints = [];
    for (let i = 0; i < penPoints.length; i += 2) {
      relativePoints.push(penPoints[i] - cx, penPoints[i + 1] - cy);
    }

    dispatch({
      type: 'ADD_OBJECT',
      payload: {
        layerId: targetLayer,
        object: {
          id: `obj-path-${Date.now()}`,
          type: 'path',
          name: 'Path',
          x: cx, y: cy,
          width: Math.max(...xs) - Math.min(...xs),
          height: Math.max(...ys) - Math.min(...ys),
          rotation: 0, scaleX: 1, scaleY: 1, opacity: 1,
          fill: '#ffffff',
          stroke: '#ffffff',
          strokeWidth: 3,
          points: relativePoints,
          keyframes: [
            { frame: 0, x: cx, y: cy, rotation: 0, scaleX: 1, scaleY: 1, opacity: 1 }
          ]
        }
      }
    });

    setPenPoints([]);
    setIsPenDrawing(false);
    dispatch({ type: 'SET_TOOL', payload: 'select' });
  };

  // Frame capture for export
  useEffect(() => {
    const handleCapture = async (e) => {
      if (!stageRef.current) return;
      const frames = [];
      const total = state.project.totalFrames;
      const fps = state.project.fps || 24;
      const step = e.detail?.step || 1;
      const pixelRatio = e.detail?.pixelRatio || 1;
      const effectiveFps = Math.max(1, Math.round(fps / step));

      // Higher JPEG quality for better output; scale with DPI
      const jpegQuality = pixelRatio >= 2 ? 0.85 : 0.75;

      dispatch({ type: 'SELECT_OBJECT', payload: null });
      dispatch({ type: 'SET_PLAYING', payload: false });

      await new Promise(r => setTimeout(r, 150));

      const totalSteps = Math.ceil(total / step) + 1;
      let captured = 0;

      for (let f = 0; f <= total; f += step) {
        dispatch({ type: 'SET_FRAME', payload: f });
        // Yield to main thread every frame so browser stays responsive
        await new Promise(r => requestAnimationFrame(() => requestAnimationFrame(r)));
        try {
          const dataUrl = stageRef.current.toDataURL({ pixelRatio, mimeType: 'image/jpeg', quality: jpegQuality });
          frames.push(dataUrl);
        } catch (err) {
          console.error('Frame capture error:', err);
        }
        captured++;
        window.dispatchEvent(new CustomEvent('capture-progress', { detail: Math.round((captured / totalSteps) * 50) }));
      }

      window.dispatchEvent(new CustomEvent('frames-captured', { detail: { frames, effectiveFps } }));
    };

    window.addEventListener('capture-frames', handleCapture);
    return () => window.removeEventListener('capture-frames', handleCapture);
  }, [state.project, dispatch]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKey = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      switch (e.key) {
        case 'Delete':
        case 'Backspace':
          if (state.selectedObjectId) dispatch({ type: 'REMOVE_OBJECT', payload: state.selectedObjectId });
          break;
        case ' ':
          e.preventDefault();
          dispatch({ type: 'SET_PLAYING', payload: !state.isPlaying });
          break;
        case 'v': dispatch({ type: 'SET_TOOL', payload: 'select' }); break;
        case 'Escape':
          if (isPenDrawing) { finishPenPath(); }
          break;
        case 'z':
          if (e.ctrlKey || e.metaKey) { e.preventDefault(); dispatch({ type: e.shiftKey ? 'REDO' : 'UNDO' }); }
          break;
        default: break;
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [state.selectedObjectId, state.isPlaying, dispatch, isPenDrawing, penPoints]);

  const W = state.project.width * state.zoom;
  const H = state.project.height * state.zoom;

  // Onion skin: compute previous and next frame offsets
  const onionFrames = [];
  if (state.onionSkin && !state.isPlaying) {
    const fps = state.project.fps;
    const prev = state.currentFrame - Math.round(fps / 6);
    const next = state.currentFrame + Math.round(fps / 6);
    if (prev >= 0) onionFrames.push({ frame: prev, opacity: 0.15, color: 'rgba(99,102,241,0.15)' });
    if (next <= state.project.totalFrames) onionFrames.push({ frame: next, opacity: 0.15, color: 'rgba(244,63,94,0.15)' });
  }

  return (
    <div
      ref={containerRef}
      className="w-full h-full overflow-hidden relative flex items-center justify-center"
      data-testid="canvas-stage"
      onMouseDown={handleContainerMouseDown}
      onMouseMove={handleContainerMouseMove}
      onMouseUp={handleContainerMouseUp}
      onMouseLeave={handleContainerMouseUp}
      style={{ cursor: isPanning ? 'grabbing' : state.activeTool === 'pen' ? 'crosshair' : 'default' }}
    >
      {/* Pen tool hint */}
      {state.activeTool === 'pen' && (
        <div className="absolute top-3 left-1/2 -translate-x-1/2 z-20 px-3 py-1.5 rounded-full bg-white/[0.06] backdrop-blur-sm text-[10px] font-mono text-muted-foreground" data-testid="pen-hint">
          {isPenDrawing ? 'Click to add points - Double-click or Esc to finish' : 'Click to start drawing'}
        </div>
      )}

      {/* Zoom controls overlay */}
      <div className="absolute bottom-3 right-3 z-20 flex items-center gap-1 bg-black/40 backdrop-blur-sm rounded-full px-2 py-1" data-testid="zoom-controls">
        <button
          className="w-5 h-5 flex items-center justify-center text-muted-foreground hover:text-foreground text-xs font-mono rounded-full hover:bg-white/10 transition-colors"
          onClick={() => dispatch({ type: 'ZOOM_AT', payload: { newZoom: state.zoom * 0.8 } })}
          data-testid="zoom-out-button"
        >-</button>
        <span className="text-[9px] font-mono text-muted-foreground min-w-[32px] text-center tabular-nums" data-testid="zoom-level">
          {Math.round(state.zoom * 100)}%
        </span>
        <button
          className="w-5 h-5 flex items-center justify-center text-muted-foreground hover:text-foreground text-xs font-mono rounded-full hover:bg-white/10 transition-colors"
          onClick={() => dispatch({ type: 'ZOOM_AT', payload: { newZoom: state.zoom * 1.25 } })}
          data-testid="zoom-in-button"
        >+</button>
        <div className="w-px h-3 bg-white/10 mx-0.5" />
        <button
          className="text-[9px] font-mono text-muted-foreground hover:text-foreground px-1 rounded hover:bg-white/10 transition-colors"
          onClick={() => dispatch({ type: 'RESET_VIEW' })}
          data-testid="zoom-reset-button"
        >Fit</button>
      </div>

      {/* Onion skin toggle */}
      <div className="absolute top-3 right-3 z-20" data-testid="onion-skin-controls">
        <button
          className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[9px] font-mono transition-all ${
            state.onionSkin
              ? 'bg-indigo-500/20 text-indigo-300 border border-indigo-500/30'
              : 'bg-black/40 text-muted-foreground/60 hover:text-muted-foreground backdrop-blur-sm'
          }`}
          onClick={() => dispatch({ type: 'TOGGLE_ONION_SKIN' })}
          data-testid="onion-skin-toggle"
        >
          <svg className="w-3 h-3" viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="1.5" opacity="0.4"/><circle cx="8" cy="8" r="4" stroke="currentColor" strokeWidth="1.5" opacity="0.7"/><circle cx="8" cy="8" r="2" fill="currentColor"/></svg>
          Onion
        </button>
      </div>

      <div style={{ transform: `translate(${state.panX}px, ${state.panY}px)` }}>
      <Stage
        ref={stageRef}
        width={W} height={H}
        scaleX={state.zoom} scaleY={state.zoom}
        onClick={handleDeselect}
        onTap={handleDeselect}
        onMouseDown={handleStageMouseDown}
        onMouseMove={handleStageMouseMove}
        onDblClick={handleStageDoubleClick}
        style={{
          border: '1px solid rgba(255,255,255,0.06)',
          borderRadius: '6px',
          boxShadow: '0 0 60px rgba(244, 114, 182, 0.04), 0 0 120px rgba(0,0,0,0.3)',
        }}
      >
        {/* Background */}
        <Layer listening={false}>
          <Rect x={0} y={0} width={state.project.width} height={state.project.height} fill="#141822" listening={false} />
        </Layer>

        {/* Onion skin - previous/next frames */}
        {onionFrames.map(({ frame: onionFrame, opacity: onionOpacity }) => (
          <Layer key={`onion-${onionFrame}`} listening={false} opacity={onionOpacity}>
            {state.project.layers.map(layer => {
              if (!layer.visible) return null;
              return layer.objects.map(obj => (
                <CanvasObject
                  key={`onion-${obj.id}-${onionFrame}`}
                  obj={obj}
                  frame={onionFrame}
                  isSelected={false}
                  onSelect={() => {}}
                  onDragEnd={() => {}}
                  onTransformEnd={() => {}}
                />
              ));
            })}
          </Layer>
        ))}

        {/* Object layers */}
        {state.project.layers.map(layer => {
          if (!layer.visible) return null;
          return (
            <Layer key={layer.id}>
              {layer.objects.map(obj => (
                <CanvasObject
                  key={obj.id} obj={obj} frame={state.currentFrame}
                  isSelected={state.selectedObjectId === obj.id}
                  onSelect={handleSelect}
                  onDragEnd={handleDragEnd}
                  onTransformEnd={handleTransformEnd}
                />
              ))}
            </Layer>
          );
        })}

        {/* Pen tool preview */}
        {isPenDrawing && penPoints.length >= 2 && (
          <Layer listening={false}>
            <Line
              points={penPoints}
              stroke="#f472b6"
              strokeWidth={2 / state.zoom}
              lineCap="round"
              lineJoin="round"
              tension={0.3}
              dash={[4 / state.zoom, 4 / state.zoom]}
            />
            {Array.from({ length: penPoints.length / 2 }).map((_, i) => (
              <Circle
                key={i}
                x={penPoints[i * 2]}
                y={penPoints[i * 2 + 1]}
                radius={3 / state.zoom}
                fill="#f472b6"
              />
            ))}
          </Layer>
        )}
      </Stage>
      </div>
    </div>
  );
}
